import {
  afterNextRender,
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDateParserFormatter, NgbDate, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, Subscription, forkJoin, takeUntil } from 'rxjs';
import moment from 'moment';

// Constants
import { DriveCampaignColor, DriveCampaignStatus, ProjectStatus } from '../../layouts/my-projects/project.const';
import { TaskStatus } from '../../shared/components/task/task-status.const';
import {
  CAMPAIGN_WORKSPACE_COMPONENT_NAMES,
  CAMPAIGN_WORKSPACE_CONSTANTS,
  CAMPAIGN_WORKSPACE_PROJECT_TYPES,
  CAMPAIGN_WORKSPACE_ROUTES,
  CAMPAIGN_WORKSPACE_TRANSLATIONS,
} from '../../shared/models/campaign-workspace-translations';
import {
  BrandUpdatedEvent,
  CalendarFilterUpdatedEvent,
  ProjectArchive,
  ProjectConfirmArchive,
  ProjectDeleteConfirm,
  ProjectDeleteTitle,
} from '../../shared/models';

// Services
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { AuthService } from '../../shared/services/auth.service';
import { CryptoService } from '../../shared/services/crypto.service';
import { CampaignName, HelperService } from '../../shared/services/helper.service';
import { NotificationService } from '../../shared/services/notification.service';
import { OverlayService } from '../../shared/services/overlayService';
import { PermissionService } from '../../shared/services/permissions.service';
import { ProjectService } from '../../shared/services/project.service';
import { TimelineService } from '../../shared/services/timeline.service';
import { GoogleAnalyticsService } from '../../shared/services/google-analytics.service';
import { ThemeService } from '../../shared/services/theme.service';
import { MtmBreadcrumbLink, MtmBreadcrumbService } from '../../shared/services/mtm-breadcrumb.service';
import { EmitterService } from '../../shared/services/emitter.service';
import { FeatureConfigService } from '../../shared/services/feature-config.service';
import { USER_PROJECTS_PAGE_SIZE } from '../../shared/utils/paged-fetch';
import { canManageProjectItem } from '../../shared/utils/permission-checks';
import { PermissionAction, type PermissionActionValue } from '../../shared/constants';

// Components
import { ProjectCompletionDateComponent } from '../../layouts/my-projects/project-completion-date/project-completion-date.component';

// Interfaces
import {
  FeatureTypes,
  ItemPermissionEntry,
  ItemPermissionsResponse,
  Project,
  WorkspaceDTO,
} from '../../shared/interfaces';
import { CustomDateParserFormatter } from '../../shared/services/datepicker-adapter';

// constants
export enum projectTypeOptions {
  STAND_ALONE_PROJECTS = 'Stand Alone Projects',
  DRIVE_CAMPAIGN = 'Drive Campaign Projects',
  CAMPAIGN = 'Campaign Projects',
}

/**
 * ProjectsComponent displays and manages projects in different views:
 * - All projects
 * - Favorite projects
 * - Archived projects
 *
 * Used in the main workspace area when Projects is selected from sidebar
 */
@Component({
  selector: 'mtm-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss', '../campaign-drive/campaign-drive.component.scss'],
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
  standalone: false,
})
export class ProjectsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchPanel', { static: false })
  private readonly searchPanel: TemplateRef<any>;

  // The sentinel lives inside an @if, so the setter re-fires whenever it enters/leaves the DOM.
  @ViewChild('loadMoreSentinel')
  set loadMoreSentinel(el: ElementRef<HTMLElement> | undefined) {
    this.sentinelEl = el?.nativeElement;
    this.observeLoadMoreSentinel(this.sentinelEl);
  }

  @Input() workspaceId: string;
  @Input() isProjectCollapse: boolean = false;
  @Input() isCampaignProjects: boolean = false;

  // Constants
  readonly projectType = Object.values(projectTypeOptions);
  readonly taskStatus = TaskStatus;
  readonly projectStatusList = ProjectStatus;
  readonly projectColor = DriveCampaignColor;
  readonly projectStatus = DriveCampaignStatus;

  private readonly projectsSignal = signal<Project[]>([]);
  private readonly workspacesSignal = signal<any[]>([]);
  private readonly userBrandsSignal = signal<any[]>([]);
  private readonly currentBrandSignal = signal<any>(null);
  private readonly projectSearchSignal = signal<string>('');
  private readonly activeTabSignal = signal<string>('all');
  private readonly isStandAloneSignal = signal<boolean>(false);
  private readonly isLoadedSignal = signal<boolean>(false);
  private readonly isErrorSignal = signal<boolean>(false);
  private readonly isAllSignal = signal<boolean>(true);
  private readonly isFavSignal = signal<boolean>(false);
  private readonly isArchivedSignal = signal<boolean>(false);
  private readonly isAllProjectSignal = signal<boolean>(true);
  private readonly isFavoritesProjectSignal = signal<boolean>(false);
  private readonly isArchivedProjectSignal = signal<boolean>(false);

  // Public properties
  get projects(): Project[] {
    return this.projectsSignal();
  }
  set projects(value: Project[]) {
    this.projectsSignal.set(value ?? []);
  }

  get workspaces(): any[] {
    return this.workspacesSignal();
  }
  set workspaces(value: any[]) {
    this.workspacesSignal.set(value ?? []);
  }

  get userBrands(): any[] {
    return this.userBrandsSignal();
  }
  set userBrands(value: any[]) {
    this.userBrandsSignal.set(value ?? []);
  }

  get currentBrand(): any {
    return this.currentBrandSignal();
  }
  set currentBrand(value: any) {
    this.currentBrandSignal.set(value ?? null);
  }

  selectedProjectType: string = projectTypeOptions.STAND_ALONE_PROJECTS;
  selectedWorkspace: any = { name: CAMPAIGN_WORKSPACE_TRANSLATIONS.ALL_WORKSPACES, id: null };
  get projectSearch(): string {
    return this.projectSearchSignal();
  }
  set projectSearch(value: string) {
    this.projectSearchSignal.set(value ?? '');
  }

  calendarViewMode = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.LAST_UPDATED;
  gotoLabel: string;
  get activeTab(): string {
    return this.activeTabSignal();
  }
  set activeTab(value: string) {
    this.activeTabSignal.set(value ?? 'all');
  }

  filteredDates: any;
  cacheBuster = new Date().getTime().toString();

  // State flags
  get isStandAlone(): boolean {
    return this.isStandAloneSignal();
  }
  set isStandAlone(value: boolean) {
    this.isStandAloneSignal.set(!!value);
  }

  isWorkspaceEnabled: boolean;
  isAdminUser: boolean = false;
  isAccountOwner: boolean = false;
  canUpdateProject: boolean = false;
  canDeleteProject: boolean = false;
  private readonly itemPermissionsSignal = signal<ReadonlyMap<string, ItemPermissionEntry>>(new Map());
  private readonly pendingItemPermissionIds = new Set<string>();
  get isLoaded(): boolean {
    return this.isLoadedSignal();
  }
  set isLoaded(value: boolean) {
    this.isLoadedSignal.set(!!value);
  }

  get isError(): boolean {
    return this.isErrorSignal();
  }
  set isError(value: boolean) {
    this.isErrorSignal.set(!!value);
  }

  canCreateProject: boolean = false;

  // View state
  get isAll(): boolean {
    return this.isAllSignal();
  }
  set isAll(value: boolean) {
    this.isAllSignal.set(!!value);
  }

  get isFav(): boolean {
    return this.isFavSignal();
  }
  set isFav(value: boolean) {
    this.isFavSignal.set(!!value);
  }

  get isArchived(): boolean {
    return this.isArchivedSignal();
  }
  set isArchived(value: boolean) {
    this.isArchivedSignal.set(!!value);
  }

  get isAllProject(): boolean {
    return this.isAllProjectSignal();
  }
  set isAllProject(value: boolean) {
    this.isAllProjectSignal.set(!!value);
  }

  get isFavoritesProject(): boolean {
    return this.isFavoritesProjectSignal();
  }
  set isFavoritesProject(value: boolean) {
    this.isFavoritesProjectSignal.set(!!value);
  }

  get isArchivedProject(): boolean {
    return this.isArchivedProjectSignal();
  }
  set isArchivedProject(value: boolean) {
    this.isArchivedProjectSignal.set(!!value);
  }

  // Private properties
  private backupProjects: Project[] = [];
  private favProjects: Project[] = [];
  private favBackupProjects: Project[] = [];
  private archiveBackupProjects: Project[] = [];
  private archiveProjects: Project[] = [];
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly subscription: Subscription = new Subscription();
  authUser: any;

  // Infinite scroll state (only the standalone, non-workspace listings are paged by the backend).
  // Signal-backed like the rest of the template state: this app is not zone-driven, so a plain
  // field flipped from the IntersectionObserver callback would not render until some later signal
  // write happened to schedule change detection — the spinner only showed by coincidence.
  private readonly hasMorePagesSignal = signal<boolean>(false);
  private readonly isLoadingMoreSignal = signal<boolean>(false);
  get hasMorePages(): boolean {
    return this.hasMorePagesSignal();
  }
  set hasMorePages(value: boolean) {
    this.hasMorePagesSignal.set(!!value);
  }
  get isLoadingMore(): boolean {
    return this.isLoadingMoreSignal();
  }
  set isLoadingMore(value: boolean) {
    this.isLoadingMoreSignal.set(!!value);
  }
  private currentPage = 1;
  private lastFavorites: any[] = [];
  private loadMoreObserver?: IntersectionObserver;
  private sentinelEl?: HTMLElement;
  private destroyed = false;
  /** Bumped on every fresh listing load; in-flight page responses from older listings are dropped. */
  private loadGeneration = 0;
  /**
   * Fast responses would flash the spinner for a frame or two — keep it visible at least this long,
   * counted from the render that shows it (see loadPage), not from the request start.
   */
  private static readonly LOAD_MORE_MIN_SPINNER_MS = 600;

  /** Only the standalone, non-workspace listings are paged; favorites and workspace-scoped load in one shot. */
  private get pagedListing(): boolean {
    return !this.isFav && !this.workspaceId;
  }

  // Services
  private readonly featureConfigService = inject(FeatureConfigService);
  private readonly injector: Injector = inject(Injector);
  protected readonly isAssetListEnabled = toSignal(
    this.featureConfigService.isFeatureActive$(FeatureTypes.FEA_CAMPAIGN_ASSETLIST),
    { initialValue: false }
  );
  constructor(
    private readonly authService: AuthService,
    public timelineService: TimelineService,
    private readonly permissionService: PermissionService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly projectService: ProjectService,
    private readonly router: Router,
    private readonly analyticService: AnalyticsService,
    private readonly notificationService: NotificationService,
    private readonly overlayService: OverlayService,
    private readonly translate: TranslatePipe,
    private readonly cryptoService: CryptoService,
    private readonly route: ActivatedRoute,
    private readonly modalService: NgbModal,
    private readonly themeService: ThemeService,
    private readonly helperService: HelperService,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService
  ) {
    this.setupEventListeners();
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.cleanupResources();
  }

  ngAfterViewInit(): void {
    this.setupView();
  }

  // ----------------------------
  // Initialization Methods
  // ----------------------------

  private initializeComponent(): void {
    if (!this.isCampaignProjects) {
      this.setBreadcrumb();
    }
    this.handleRouteParams();
    this.setCurrentSelectedBrand();
    this.setupUserPermissions();
    this.initializeAuthUser();
    this.setupTimelineService();
    this.initializeProjectCreationPermissions();
    this.loadInitialProjects();
    this.loadFeatureConfigs();
  }

  private setupEventListeners(): void {
    EmitterService.get(BrandUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          if (!this.isCampaignProjects) {
            this.setBreadcrumb();
          }
          this.setCurrentSelectedBrand();
        },
      });

    EmitterService.get(CalendarFilterUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          this.filteredDates = res;
          this.getStandAloneProjects();
        },
      });
  }

  private handleRouteParams(): void {
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((query) => {
      if (query.wid) {
        this.workspaceId = this.cryptoService.decryptUsingAES256(query.wid);
      }
    });
  }

  private setupUserPermissions(): void {
    this.isAdminUser = this.permissionService.isPlatformAdmin();
    this.isAccountOwner = this.permissionService.isAccountManager();
    this.canUpdateProject = this.permissionService.canUpdateProject();
    this.canDeleteProject = this.permissionService.canDeleteProject();
  }

  private initializeAuthUser(): void {
    this.authUser = this.authService.getAuthUser();
  }

  private setupTimelineService(): void {
    this.timelineService.isDriveNav.emit(true);
    const brand = this.authService.getMyBrand();
    this.timelineService.componentName.next({
      componentName: CAMPAIGN_WORKSPACE_TRANSLATIONS.PROJECTS_COMPONENT,
      bannerUrl: brand?.banner,
    });
  }

  private initializeProjectCreationPermissions(): void {
    this.canCreateProject =
      this.permissionService.isPlatformAdmin() || (this.permissionService.canCreateProject() && !this.isAgencyUser);
  }

  private loadInitialProjects(): void {
    if (this.selectedProjectType === projectTypeOptions.STAND_ALONE_PROJECTS) {
      this.isStandAlone = true;
      this.getStandAloneProjects();
    }
    this.getTabsInputs();
    this.getWorkspaces();
  }

  private loadFeatureConfigs(): void {
    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_MAIN, FeatureTypes.FEA_WORKSPACE_MAIN])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.gotoLabel = this.helperService.translateConfigCampaignNameToLocalizationKey(
            response[FeatureTypes.FEA_CAMPAIGN_MAIN]?.campaignName as CampaignName,
            'gotoCampaign'
          );
          this.isWorkspaceEnabled = response[FeatureTypes.FEA_WORKSPACE_MAIN]?.active;
        },
      });
  }

  private setupView(): void {
    if (this.isCampaignProjects) {
      return;
    }
    this.themeService.updateContent({
      searchPanel: this.searchPanel,
      fullWidthContent: true,
    });
  }

  private cleanupResources(): void {
    this.destroyed = true;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.subscription.unsubscribe();
    this.loadMoreObserver?.disconnect();
    if (this.isCampaignProjects) {
      return;
    }
    this.mtmBreadcrumbService.setLinks([]);
    this.themeService.updateContent({
      searchPanel: null,
      standaloneSearch: null,
      fullWidthContent: false,
    });
  }

  // ----------------------------
  // Project Loading Methods
  // ----------------------------

  getStandAloneProjects(): void {
    this.resetProjectState();
    this.resetPagingState();

    if (this.workspaceId) {
      this.analyticService
        .getStandaloneProjectInWorkspace(this.workspaceId, this.isStandAlone, this.filteredDates)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (data) => this.handleProjectsResponse(data),
          error: (err) => this.handleProjectsError(err),
        });
      return;
    }

    this.loadProjectsPage(1);
  }

  getStandAloneArchiveProjects(): void {
    this.resetProjectState();
    this.resetPagingState();

    if (this.workspaceId) {
      this.projectService
        .getArchivedProjectsByWorkspace(this.workspaceId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (data: any) => this.appendArchivePage(data, 1, false),
          error: (err) => this.handleProjectsError(err),
        });
      return;
    }

    this.loadArchivePage(1);
  }

  /** Sentinel handler — loads the next page of the active paged listing. */
  loadMoreProjects(): void {
    if (this.isLoadingMore || !this.hasMorePages || this.workspaceId) {
      return;
    }
    if (this.isArchived) {
      this.loadArchivePage(this.currentPage + 1);
    } else if (this.isAll) {
      this.loadProjectsPage(this.currentPage + 1);
    }
  }

  private loadProjectsPage(page: number): void {
    this.loadPage(
      page,
      this.analyticService.getProjectsUser(this.authUser.email, false, this.workspaceId, true, page),
      (data, p) => this.appendProjectsPage(data, p)
    );
  }

  private loadArchivePage(page: number): void {
    this.loadPage(page, this.projectService.getArchivedProjects(page), (data, p) =>
      this.appendArchivePage(data, p, true)
    );
  }

  /**
   * Shared load-more plumbing: spinner state, the minimum spinner display time, and a generation
   * guard so a response (or its deferred apply) that lands after the listing was reloaded or the
   * tab switched is dropped instead of clobbering the freshly-loaded list.
   */
  private loadPage(page: number, request: Observable<any>, apply: (data: any, page: number) => void): void {
    this.isLoadingMore = page > 1;
    // The minimum display window counts from the render that shows the spinner, not from the request
    // start: that change-detection tick re-checks every loaded card, so on long listings it lands well
    // after the request went out and would otherwise eat into the window.
    let shownAt: number = Date.now();
    if (this.isLoadingMore) {
      afterNextRender(
        () => {
          shownAt = Date.now();
        },
        { injector: this.injector }
      );
    }
    const generation = this.loadGeneration;
    request.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (data: any) =>
        this.applyAfterMinSpinnerTime(shownAt, () => {
          if (generation === this.loadGeneration) {
            apply(data, page);
          }
        }),
      error: (err: any) => {
        if (generation !== this.loadGeneration) {
          return;
        }
        if (page === 1) {
          this.handleProjectsError(err);
        } else {
          this.handleLoadMoreError(err);
        }
      },
    });
  }

  /** Load-more appends wait out the spinner's minimum display time (from shownAt); page 1 applies immediately. */
  private applyAfterMinSpinnerTime(shownAt: number, apply: () => void): void {
    const remaining = this.isLoadingMore
      ? Math.max(0, ProjectsComponent.LOAD_MORE_MIN_SPINNER_MS - (Date.now() - shownAt))
      : 0;
    if (!remaining) {
      apply();
      return;
    }
    setTimeout(() => {
      if (!this.destroyed) {
        apply();
      }
    }, remaining);
  }

  private appendProjectsPage(data: Project[], page: number): void {
    // Merge the raw page: mergePage's end-of-listing rule reads the unfiltered page length.
    const merged = this.mergePage(this.favProjects, Array.isArray(data) ? data : [], page, true);
    this.favProjects = merged;
    this.favBackupProjects = merged;
    this.projects = this.withoutArchived(merged);
    this.backupProjects = this.projects;
    this.finishPageApply(page);
  }

  private appendArchivePage(data: any, page: number, paged: boolean): void {
    if (!paged && !Array.isArray(data)) return;

    const items = (Array.isArray(data) ? data : []).map((project) => ({
      ...project,
      avatarUrl: project.avatarUrl ? `${project.avatarUrl}?${new Date().getTime()}` : null,
    }));
    const merged = this.mergePage(this.archiveProjects, items, page, paged);
    this.projects = merged;
    this.backupProjects = merged;
    this.archiveProjects = merged;
    this.archiveBackupProjects = merged;
    this.finishPageApply(page);
  }

  /** Merges a page into the accumulator (Set-based dedupe by id) and derives the paging state. */
  private mergePage(accumulator: Project[], items: Project[], page: number, paged: boolean): Project[] {
    const previous = page === 1 ? [] : accumulator;
    const seen = new Set(previous.map((p) => p.id));
    const merged = previous.concat(items.filter((p) => !seen.has(p.id)));
    this.currentPage = page;
    this.isLoadingMore = false;
    // End of listing: a short page (backend contract, same rule as fetchAllPages) or a page
    // contributing nothing new (backstop for a backend that ignores the page param).
    this.hasMorePages = paged && items.length >= USER_PROJECTS_PAGE_SIZE && merged.length > previous.length;
    return merged;
  }

  private finishPageApply(page: number): void {
    if (page === 1) {
      this.processProjects();
      this.getFavoriteProjects();
    } else {
      this.handleFavoriteProjectsResponse(this.lastFavorites);
      this.searchProject();
    }
    this.nudgeLoadMoreCheck();
  }

  private resetPagingState(): void {
    this.currentPage = 1;
    this.hasMorePages = false;
    this.isLoadingMore = false;
  }

  private observeLoadMoreSentinel(el?: HTMLElement): void {
    this.loadMoreObserver?.disconnect();
    if (!el) {
      return;
    }
    this.loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          // Rendering is scheduled by the signal-backed paging flags flipped in loadMoreProjects;
          // wrapping this in NgZone.run rendered nothing because the app is not zone-driven.
          this.loadMoreProjects();
        }
      }
      // No rootMargin or threshold on purpose: the observed element is the 1px strip on the row's
      // bottom edge (.load-more-sentinel), so "intersecting" means the whole spinner is on screen; a
      // prefetch margin would let the fetch complete off-screen and the spinner would never be seen.
    );
    this.loadMoreObserver.observe(el);
  }

  /**
   * IntersectionObserver only fires on visibility crossings, so when an appended page leaves the
   * sentinel in view no new event arrives and paging stalls. Re-observing forces one fresh
   * post-layout reading after every append: still visible → next page loads, otherwise wait for scroll.
   */
  private nudgeLoadMoreCheck(): void {
    if (this.sentinelEl && this.loadMoreObserver) {
      this.loadMoreObserver.unobserve(this.sentinelEl);
      this.loadMoreObserver.observe(this.sentinelEl);
    }
  }

  getFavoriteProjects(): void {
    // Only the favorites tab renders straight from this response; on All/Archived it just
    // refreshes the star flags, so keep the already-rendered cards instead of flashing the skeleton.
    if (this.isFav) {
      this.resetProjectState();
    }

    let type: string;
    switch (this.selectedProjectType) {
      case projectTypeOptions.CAMPAIGN:
        type = CAMPAIGN_WORKSPACE_PROJECT_TYPES.CAMPAIGN;
        break;
      case projectTypeOptions.DRIVE_CAMPAIGN:
        type = CAMPAIGN_WORKSPACE_PROJECT_TYPES.DRIVE_CAMPAIGN;
        break;
      default:
        type = CAMPAIGN_WORKSPACE_PROJECT_TYPES.STANDALONE_PROJECT;
        break;
    }

    this.analyticService
      .getFavoriteItem(type)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any) => this.handleFavoriteProjectsResponse(res),
        error: (err) => this.handleProjectsError(err),
      });
  }

  private handleFavoriteProjectsResponse(res: any): void {
    // Kept for the paged listings: appended pages re-apply the favorite flags without a refetch.
    this.lastFavorites = Array.isArray(res) ? res : [];

    if (this.isFav) {
      this.hasMorePages = false; // the favorites endpoint is not paged
      this.projects = this.workspaceId ? res.filter((project: any) => project.workspaceId === this.workspaceId) : res;

      this.backupProjects = [...this.projects];
      this.projects.forEach((project) => (project.isFavorite = true));
      this.backupProjects.forEach((project) => (project.isFavorite = true));
    } else if (this.isAll) {
      this.updateFavoriteStatus(this.favProjects, res);
      this.updateFavoriteStatus(this.favBackupProjects, res);

      this.projects = this.withoutArchived(this.favProjects).filter(this.removeDuplicates);
      this.backupProjects = this.withoutArchived(this.favBackupProjects).filter(this.removeDuplicates);
    } else if (this.isArchived) {
      this.updateFavoriteStatus(this.archiveProjects, res);
      this.updateFavoriteStatus(this.archiveBackupProjects, res);

      this.projects = this.archiveProjects.filter(this.removeDuplicatesWithWorkspace);
      this.backupProjects = this.archiveBackupProjects.filter(this.removeDuplicatesWithWorkspace);
    }

    this.processProjects();
  }

  private updateFavoriteStatus(projects: Project[], favorites: any[]): void {
    projects.forEach((project) => {
      const foundProject = favorites.find((w) => w.id === project.id);
      project.isFavorite = foundProject !== undefined;
    });
  }

  private readonly removeDuplicates = (value: Project, index: number, self: Project[]) =>
    index === self.findIndex((t) => t.id === value.id);

  private readonly removeDuplicatesWithWorkspace = (value: Project, index: number, self: Project[]) =>
    index === self.findIndex((t) => t.id === value.id && (!this.workspaceId || t.workspaceId === this.workspaceId));

  /** The All tab never lists archived projects — not even for a frame while the favorites response is pending. */
  private readonly withoutArchived = (projects: Project[]): Project[] =>
    projects.filter((p) => p.status !== ProjectStatus.ARCHIVED);

  private handleProjectsResponse(data: Project[]): void {
    const active = this.withoutArchived(data);
    this.projects = active;
    this.backupProjects = active;
    this.favBackupProjects = active;
    this.favProjects = active;
    this.processProjects();
    this.getFavoriteProjects();
  }

  private processProjects(): void {
    // Paged listings keep the backend's page order: re-sorting on every append shuffles the
    // cards under the user mid-scroll, and a global sort is impossible with partial data anyway.
    if (!this.pagedListing) {
      this.projects.sort((p1, p2) => p1.name.localeCompare(p2.name));
      this.backupProjects.sort((p1, p2) => p1.name.localeCompare(p2.name));
    }
    this.isLoaded = true;
    this.refreshItemPermissions();
  }

  /** Fetches CORE_PROJECT per-item permissions for any rendered project id not already resolved or
   *  in flight. Permissions are id-keyed, not tied to the current tab/filter, so a reload or tab
   *  switch reuses what's already known instead of refetching. The pending-set exists because
   *  processProjects() fires twice in quick succession on a fresh load (once for the project-list
   *  response, again when the favorites response lands) — without it, the second call would see the
   *  same unresolved ids and fire a duplicate request. */
  private refreshItemPermissions(): void {
    const known = this.itemPermissionsSignal();
    const newIds = Array.from(
      new Set(
        this.projects
          .map((p) => p.id)
          .filter((id): id is string => !!id && !known.has(id) && !this.pendingItemPermissionIds.has(id))
      )
    );
    if (!newIds.length) {
      return;
    }
    newIds.forEach((id) => this.pendingItemPermissionIds.add(id));
    this.permissionService
      .getProjectItemPermissions(newIds)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response: ItemPermissionsResponse) => {
        const merged = new Map(this.itemPermissionsSignal());
        newIds.forEach((id) => {
          merged.set(id, response[id] ?? { access: false });
          this.pendingItemPermissionIds.delete(id);
        });
        this.itemPermissionsSignal.set(merged);
      });
  }

  private handleProjectsError(err: any): void {
    console.error(err);
    this.isError = true;
  }

  /** A failed load-more must not kill paging: clear the spinner, keep the sentinel so the next scroll retries. */
  private handleLoadMoreError(err: any): void {
    console.error(err);
    this.isLoadingMore = false;
  }

  private resetProjectState(): void {
    this.loadGeneration++; // any in-flight page load now belongs to a stale listing
    this.projects = [];
    this.backupProjects = [];
    this.isLoaded = false;
    this.isError = false;
  }

  // ----------------------------
  // Tab Management
  // ----------------------------

  private getTabsInputs(): void {
    this.setupTabListener(this.timelineService.isAll, () => {
      this.isAll = true;
      this.isFav = false;
      this.isArchived = false;
      if (this.selectedProjectType === projectTypeOptions.STAND_ALONE_PROJECTS) {
        this.getStandAloneProjects();
      }
      this.timelineService.emitIsAll(false);
    });

    this.setupTabListener(this.timelineService.isFavorites, () => {
      this.isAll = false;
      this.isFav = true;
      this.isArchived = false;
      this.getFavoriteProjects();
      this.timelineService.emitIsFavorites(false);
    });

    this.setupTabListener(this.timelineService.isArchived, () => {
      this.isAll = false;
      this.isFav = false;
      this.isArchived = true;
      if (this.selectedProjectType === projectTypeOptions.STAND_ALONE_PROJECTS) {
        this.getStandAloneArchiveProjects();
      }
      this.timelineService.emitIsArchived(false);
    });
  }

  private setupTabListener(observable: any, callback: () => void): void {
    observable.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (data: boolean) => data && callback(),
      error: (err: any) => console.error(err),
    });
  }

  // ----------------------------
  // Project Actions
  // ----------------------------

  markAsFavorite(project: Project): void {
    if (this.selectedProjectType !== projectTypeOptions.STAND_ALONE_PROJECTS) return;

    this.analyticService
      .markAsFavorite(project, CAMPAIGN_WORKSPACE_PROJECT_TYPES.STANDALONE_PROJECT)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any) => {
          this.projects = this.projects.map((p) => ({
            ...p,
            isFavorite: res.items.includes(p.id),
          }));
        },
        error: (err) => {
          console.error(err);
          project.isFavorite = false;
        },
      });
  }

  removeFavorite(project: Project): void {
    this.analyticService
      .removeAsFavorite(project, CAMPAIGN_WORKSPACE_PROJECT_TYPES.STANDALONE_PROJECT)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.getStandAloneProjects();
        },
        error: (err) => console.error(err),
      });
  }

  changeProjectStatus(project: Project, status: string): void {
    if (!this.checkProjectStatusChangePermission(project)) return;

    const oldStatus = project.status;

    if (status === ProjectStatus.Completed) {
      this.handleProjectCompletion(project, oldStatus);
    } else {
      project.status = status;
      this.saveProjectStatus(project, status, oldStatus);
    }
  }

  canUpdateProjectRow(project: Project): boolean {
    return this.canManageProjectRow(project, this.canUpdateProject, PermissionAction.UPDATE);
  }

  canDeleteProjectRow(project: Project): boolean {
    return this.canManageProjectRow(project, this.canDeleteProject, PermissionAction.DELETE);
  }

  private canManageProjectRow(project: Project, hasGlobalCapability: boolean, action: PermissionActionValue): boolean {
    return canManageProjectItem(
      this.isAdminUser,
      this.isAccountOwner,
      project,
      this.authUser?.username,
      hasGlobalCapability,
      this.itemCapability(project, action)
    );
  }

  private itemCapability(project: Project, action: PermissionActionValue): boolean {
    const entry = project.id ? this.itemPermissionsSignal().get(project.id) : undefined;
    return this.permissionService.hasItemCapability(entry, action);
  }

  private checkProjectStatusChangePermission(project: Project): boolean {
    const isProjectOwner = project.manager === this.authUser.username;
    const isCompanyOwner = project.company?.id === this.authUser.companyId && this.isAccountOwner;
    return (
      this.isAdminUser ||
      isProjectOwner ||
      isCompanyOwner ||
      this.canUpdateProject ||
      this.itemCapability(project, PermissionAction.UPDATE)
    );
  }

  private handleProjectCompletion(project: Project, oldStatus: string): void {
    const modalRef = this.modalService.open(ProjectCompletionDateComponent, { size: 'sm' });
    modalRef.componentInstance.setParams({ project });

    modalRef.result.then((data: any) => {
      if (!data) return;

      project.status = ProjectStatus.Completed;
      if (data.completionDate) {
        project.completionDate = data.completionDate;
      }
      this.saveProjectStatus(project, ProjectStatus.Completed, oldStatus);
    });
  }

  private saveProjectStatus(project: Project, status: string, oldStatus: string): void {
    const updateArgs: any = {
      projectId: project.id,
      status,
    };

    if (status === ProjectStatus.Completed && project.completionDate) {
      updateArgs.completionDate = moment(project.completionDate).format('DD-MM-yyyy');
    }

    this.projectService
      .updateProjectStatus(updateArgs)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: any) => {
          project.status = result.status;
          project.completionDate = result.completionDate;
        },
        error: () => {
          project.status = oldStatus;
          this.overlayService.showError(this.translate.transform('overlaySaveError'), 'Error');
        },
      });
  }

  archiveRestoreProject(project: Project): void {
    if (!project.deleted) {
      this.confirmArchiveProject(project);
    } else {
      this.restoreProject(project);
    }
  }

  private confirmArchiveProject(project: Project): void {
    this.notificationService
      .open({
        title: this.translate.transform(ProjectArchive),
        description: this.translate.transform(ProjectConfirmArchive, { name: project.name }),
        confirmBtn: this.translate.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.YES),
        cancelBtn: this.translate.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.CANCEL),
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirm: any) => {
        if (!confirm) return;

        this.projectService
          .deleteProject(project.id, true)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: () => {
              project.deleted = true;
              const projectIndex = this.projects.findIndex((p) => p.id === project.id);
              if (projectIndex !== -1) {
                if (this.isAll) {
                  this.projects[projectIndex].status = ProjectStatus.ARCHIVED;
                }
                this.projects.splice(projectIndex, 1);
              }
            },
            error: (err) => this.showErrorNotification(err),
          });
      });
  }

  private restoreProject(project: Project): void {
    this.projectService
      .restoreArchive(project.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: any) => {
          if (this.isArchived) {
            this.projects = this.projects.filter((p) => p.id !== result.id);
          } else {
            project.status = result.status;
            project.deleted = false;
          }
        },
        error: () => {
          this.overlayService.showError(this.translate.transform('overlayErrorSave'), 'Error');
        },
      });
  }

  deleteProject(project: Project): void {
    this.notificationService
      .open({
        title: this.translate.transform(ProjectDeleteTitle),
        description: this.translate.transform(ProjectDeleteConfirm, { project_name: project.name }),
        confirmBtn: this.translate.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.YES),
        cancelBtn: this.translate.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.CANCEL),
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirm: boolean) => {
        if (!confirm) return;

        this.projectService
          .deleteProject(project.id, !project.deleted)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: () => {
              project.deleted = !project.deleted;
              this.projects = this.projects.filter((p) => p.id !== project.id);
            },
            error: (err) => this.showErrorNotification(err),
          });
      });
  }

  private showErrorNotification(error: any): void {
    this.notificationService.open({
      title: CAMPAIGN_WORKSPACE_TRANSLATIONS.WARNING,
      description: error.message,
      confirmBtn: 'Ok',
    });
  }

  // ----------------------------
  // Workspace & Brand Methods
  // ----------------------------

  getWorkspaces(): void {
    forkJoin([this.analyticService.getBrands(), this.analyticService.getActiveBrandWorkspaces()])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ([brands, workspaces]) => this.processBrands(brands, workspaces),
        error: (err) => console.error('err', err),
      });
  }

  private processBrands(brands: any[], workspaces: WorkspaceDTO[]): void {
    this.userBrands = brands;
    this.workspaces = workspaces;

    if (this.workspaceId) {
      const workspace = this.workspaces.find((w) => w.id === this.workspaceId);
      this.selectedWorkspace = workspace || console.warn('Workspace not found with id:', this.workspaceId);
    }

    if (!this.isCampaignProjects) {
      this.themeService.updateContent({
        standaloneSearch: this.userBrands.length > 1,
      });
    }
  }

  setBreadcrumb(): void {
    const brand = JSON.parse(localStorage.getItem(CAMPAIGN_WORKSPACE_CONSTANTS.WORKSPACE_LOCAL_STORAGE_KEYS.BRAND));

    if (brand) {
      const pageLinks: MtmBreadcrumbLink[] = [
        {
          label: brand.name,
          url: ['/'],
        },
        {
          label: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.PROJECT,
        },
      ];

      this.mtmBreadcrumbService.setLinks(pageLinks);
      this.googleAnalyticsService.sendUserAnalyticsConfig('', '', '');
    }
  }

  setCurrentSelectedBrand(): void {
    this.currentBrand = JSON.parse(
      localStorage.getItem(CAMPAIGN_WORKSPACE_CONSTANTS.WORKSPACE_LOCAL_STORAGE_KEYS.BRAND)
    );
  }

  // ----------------------------
  // View Management
  // ----------------------------

  searchProject(): void {
    if (!this.projectSearch) {
      this.projects = this.backupProjects;
      return;
    }

    const searchTerm = this.projectSearch.toLowerCase();
    this.projects = this.backupProjects.filter((project) => project.name?.toLowerCase().includes(searchTerm));
  }

  // ----------------------------
  // Navigation Methods
  // ----------------------------

  createProject(): void {
    const queryParams = this.workspaceId ? { workspaceId: this.workspaceId } : {};
    this.router
      .navigate([CAMPAIGN_WORKSPACE_ROUTES.NEW_COMPANY_PROJECTS], { queryParams })
      .catch((err) => console.error('Navigation error', err));
  }

  editProject(project: Project): void {
    this.router
      .navigate([`/projects/edit/${project.id}/company`])
      .catch((err) => console.error('Navigation error', err));
  }

  redirectToDashboard(project: Project): void {
    this.router.navigate([`/projects/${project.id}`]).catch((err) => console.error('Navigation error', err));
  }

  gotoWorkspace(project: Project): void {
    if (!this.isWorkspaceEnabled) return;

    this.router
      .navigate([CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE], {
        queryParams: {
          wid: this.cryptoService.encryptString(project?.workspaceId),
        },
      })
      .catch((err) => console.error('Navigation error', err));
  }

  gotoCampaign(project: Project): void {
    this.router
      .navigate([CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE_DASHBOARD], {
        queryParams: {
          wid: this.cryptoService.encryptString(project?.workspaceId),
          dc: this.cryptoService.encryptString(project?.campaignId),
        },
      })
      .catch((err) => console.error('Navigation error', err));
  }

  // ----------------------------
  // View Toggle Methods
  // ----------------------------

  allProjectItems(): void {
    this.isArchivedProject = false;
    this.isFavoritesProject = false;
    this.isAllProject = true;
    this.isAll = true;
    this.timelineService.emitIsAll(true);
  }

  favoriteProjectItems(): void {
    this.isArchivedProject = false;
    this.isFavoritesProject = true;
    this.isAllProject = false;
    this.isFav = true;
    this.getFavoriteProjects();
  }

  getArchiveProjects(): void {
    this.isArchivedProject = true;
    this.isFavoritesProject = false;
    this.isAllProject = false;
    this.timelineService.emitIsArchived(true);
  }

  // ----------------------------
  // Helper Methods
  // ----------------------------

  get isAgencyUser(): boolean {
    return this.authUser.userPermissionType === CAMPAIGN_WORKSPACE_CONSTANTS.AGENCY;
  }

  shouldShowEllipsis(name: string, maxLength: number = 32): boolean {
    return name?.length > maxLength;
  }

  parseTimestamp(timestamp: string): NgbDate {
    const date = new Date(timestamp);
    return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }
}
